/**
 * Набор тем организации (#845, спека docs/plans/2026-10-02-conversation-themes.md
 * §2, §2.1) — боковая панель из шапки раздела «Голос клиента». Пока в наборе
 * нет активной темы, кнопка называется «Задать темы» (#2072, спека
 * 2026-10-11-voice-report-layers.md §2.6): вкладки тогда блока тем не рисуют, и
 * сказать это — дело одной кнопки в шапке. Роль без права правки открывает ту
 * же панель и видит, что набор задаёт клиент или админ. Одна и та же
 * в портале и во встроенном Bitrix24: раздел открывается в обоих, а данные
 * идут через `useAuth`.
 *
 * Тема — «о чём разговор» (модель X-200, лизинг, доставка в регионы), не
 * категория звонка и не предмет вопроса: это сказано в описании панели, а
 * совпадающее название форма предупреждает.
 *
 * Читают все, кто видит раздел; правят client и admin (Р5) — остальным та же
 * панель без кнопок, с объяснением. Кап — 30 тем `active` + `reserved`: счётчик
 * над списком, и при заполненном наборе форма добавления сменяется подсказкой
 * «архивируйте тему». Решает всё равно сервер (409 `theme_set_full`).
 *
 * Правящим — ещё и стартовое предложение (#845, PR 4, `VoiceThemeBootstrap`):
 * при пустом наборе — «Предложить темы по истории», готовое — экран утверждения;
 * и предложения недельной сводки (#845, PR 5b, `VoiceThemeProposals`): пример
 * разговора открывается тем же `onOpenCall`, что разговоры раздела, и панель
 * при этом закрывается.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tags } from 'lucide-react';
import type { OrgTheme, OrgThemeMutationResponse } from '@aica/api-types/schemas';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { VoiceThemeBootstrap } from './VoiceThemeBootstrap';
import { VoiceThemeProposals } from './VoiceThemeProposals';
import { VoiceThemeForm, type VoiceThemeFormValues } from './VoiceThemeForm';
import { VoiceThemeRow } from './VoiceThemeRow';
import {
  themeErrorMessage,
  themeRefusalMessage,
  type ThemeWriteAction,
} from './theme-error-message';
import { ApiError, isObject } from '@/lib/fetch-utils';
import { useLockedDemoReason } from '@/contexts/LockedDemoContext';
import { themeListHasActive } from '../voice-themes-view-model';
import { overlapNames } from './overlap-names';
import {
  useCanEditVoiceThemes,
  useCreateVoiceTheme,
  useSetVoiceThemeArchived,
  useUpdateVoiceTheme,
  useVoiceThemes,
} from './useVoiceThemes';

export interface VoiceThemesSheetProps {
  /** Open one conversation the way the section does (portal or embedded). */
  onOpenCall?: (recordId: string) => void;
  /**
   * Controlled open state: the themes block opens the panel from its
   * proposals line (#845 PR 6). Absent — the panel keeps its own state and
   * only the trigger button opens it.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function VoiceThemesSheet({
  onOpenCall,
  open: controlledOpen,
  onOpenChange,
}: VoiceThemesSheetProps = {}) {
  const { t } = useTranslation(['dashboard', 'records']);
  const [ownOpen, setOwnOpen] = useState(false);
  const open = controlledOpen ?? ownOpen;
  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setOwnOpen(next);
    onOpenChange?.(next);
  };
  const canEdit = useCanEditVoiceThemes();
  // The demo of a closed section: the set is read-only, and the panel says which plan opens it.
  const lockedReason = useLockedDemoReason();
  // Read with the header: an empty set turns the trigger into «Set themes»
  // (#2072) — the one place that says so instead of an empty block per tab.
  const themes = useVoiceThemes();
  const create = useCreateVoiceTheme();
  const update = useUpdateVoiceTheme();
  const setArchived = useSetVoiceThemeArchived();

  /**
   * The server's §2.1 verdict on the name just saved: the live hint in the form
   * is the same table, but the saved name is the server's — shown above the
   * list until the next write.
   */
  const [saved, setSaved] = useState<OrgThemeMutationResponse | null>(null);

  /** A failed write becomes the message the form or row shows next to its button. */
  const explained = async <T,>(
    write: () => Promise<T>,
    action: ThemeWriteAction
  ): Promise<T> => {
    setSaved(null);
    try {
      return await write();
    } catch (error) {
      throw new Error(await themeErrorMessage(error, t, action));
    }
  };
  /** A create or rename: keep the server's overlap verdict for the notice. */
  const savedName = async (
    write: () => Promise<OrgThemeMutationResponse>,
    action: ThemeWriteAction
  ) => {
    setSaved(await explained(write, action));
  };
  const data = themes.data;
  // The notice belongs to the theme as it was saved: gone once the list no
  // longer holds that theme under that name (renamed, re-read after another
  // edit), and cleared when the panel closes.
  const savedOverlaps =
    saved &&
    data?.themes.some(
      (theme) => theme.id === saved.theme.id && theme.name === saved.theme.name
    )
      ? (saved.overlaps ?? [])
      : [];

  // The list failed. A 403 is a decision (the flag, the plan, the role) —
  // asking again answers the same, so it is said in words without «Retry».
  // Anything else may pass: network, 5xx, a 429 (too many requests) and a
  // 401 (the session is renewed by the fetch layer) keep «Retry».
  const loadError = themes.error;
  // No status — the request never got an answer (network): not a refusal.
  const loadStatus = loadError instanceof ApiError ? (loadError.status ?? 0) : 0;
  const refused = loadStatus === 403;
  const loadMessage = refused
    ? themeRefusalMessage(
        loadError instanceof ApiError && isObject(loadError.data) ? loadError.data : null,
        loadStatus,
        t,
        {
          fallback: t('voicePage.themes.loadFailed'),
          forbidden: t('voicePage.themes.errors.forbiddenRead'),
        }
      )
    : t('voicePage.themes.loadFailed');
  const full = data ? data.counted >= data.cap : false;
  const live = data?.themes.filter((theme) => theme.status !== 'archived') ?? [];
  const archived = data?.themes.filter((theme) => theme.status === 'archived') ?? [];
  // No active theme: nothing is assigned (a reserved code is not yet a theme).
  // An unread or failed list keeps «Themes» — the panel says what went wrong.
  const setEmpty = data !== undefined && !themeListHasActive(data.themes);

  const row = (theme: OrgTheme) => (
    <VoiceThemeRow
      key={theme.id}
      theme={theme}
      canEdit={canEdit}
      canRestore={!full}
      onEdit={async (target, values: VoiceThemeFormValues) => {
        // `target` is the row's snapshot from when the form opened: only what
        // changed against it, with its version — a stale form cannot revert
        // another admin's edit (409 theme_changed).
        const changes = {
          ...(values.name !== target.name ? { name: values.name } : {}),
          ...(values.definition !== target.definition
            ? { definition: values.definition }
            : {}),
        };
        if (Object.keys(changes).length === 0) return;
        await savedName(
          () =>
            update.mutateAsync({
              id: target.id,
              body: { ...changes, expectedUpdatedAt: target.updatedAt },
            }),
          'edit'
        );
      }}
      onSetArchived={async (target, toArchive) => {
        await explained(
          () => setArchived.mutateAsync({ id: target.id, archived: toArchive }),
          'status'
        );
      }}
    />
  );

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSaved(null);
      }}
    >
      <SheetTrigger asChild>
        <Button variant="outline" size="sm">
          <Tags className="mr-2 h-4 w-4" aria-hidden="true" />
          {t(setEmpty ? 'voicePage.themes.setThemes' : 'voicePage.themes.open')}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{t('voicePage.themes.title')}</SheetTitle>
          <SheetDescription>{t('voicePage.themes.description')}</SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-6">
          {themes.isPending ? (
            <p className="text-sm text-fg-muted" role="status">
              {t('voicePage.themes.loading')}
            </p>
          ) : !data ? (
            <div className="space-y-3" role="alert">
              <p className="text-sm text-danger">{loadMessage}</p>
              {!refused && (
                <Button variant="outline" size="sm" onClick={() => void themes.refetch()}>
                  {t('voicePage.themes.retry')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <p className="text-sm text-fg-muted">
                {t('voicePage.themes.counter', { counted: data.counted, cap: data.cap })}
              </p>

              {/* #1608, #2031: a Uspacy portal on an instruction that does not
                  name themes — its calls are left out, and the panel says why
                  and where to lift it rather than staying silently empty. */}
              {data.uspacyExcludedPortals && data.uspacyExcludedPortals.length > 0 && (
                <p
                  role="status"
                  data-testid="voice-themes-uspacy-excluded"
                  className="rounded-card border border-warning/30 bg-warning-dim px-3 py-2 text-sm text-warning-fg"
                >
                  {t('voicePage.themes.uspacyExcluded', {
                    portals: data.uspacyExcludedPortals.join(', '),
                  })}
                </p>
              )}

              {saved && savedOverlaps.length > 0 && (
                <p
                  role="status"
                  className="rounded-card border border-warning/30 bg-warning-dim px-3 py-2 text-sm text-warning-fg"
                >
                  {t('voicePage.themes.savedOverlap', {
                    name: saved.theme.name,
                    names: overlapNames(savedOverlaps, t),
                  })}
                </p>
              )}

              {/* The starter proposal (#845 PR 4): only for the set's editors. */}
              {canEdit && <VoiceThemeBootstrap setEmpty={live.length === 0} />}

              {/* The weekly proposals (#845 PR 5b): only for the set's editors. */}
              {canEdit && (
                <VoiceThemeProposals
                  {...(onOpenCall
                    ? {
                        onOpenCall: (recordId: string) => {
                          setOpen(false);
                          onOpenCall(recordId);
                        },
                      }
                    : {})}
                />
              )}

              {!canEdit ? (
                <p className="text-sm text-fg-muted">
                  {lockedReason ?? t('voicePage.themes.readOnly')}
                </p>
              ) : full ? (
                <p className="rounded-card border border-border bg-bg-elevated px-3 py-2 text-sm text-fg">
                  {t('voicePage.themes.full')}
                </p>
              ) : (
                <section className="space-y-3" aria-labelledby="voice-themes-add">
                  <h3 id="voice-themes-add" className="text-sm font-semibold text-fg">
                    {t('voicePage.themes.addTitle')}
                  </h3>
                  <VoiceThemeForm
                    submitLabel={t('voicePage.themes.form.add')}
                    resetOnSuccess
                    onSubmit={(values) =>
                      savedName(() => create.mutateAsync(values), 'create')
                    }
                  />
                </section>
              )}

              <section className="space-y-3" aria-labelledby="voice-themes-live">
                <h3 id="voice-themes-live" className="text-sm font-semibold text-fg">
                  {t('voicePage.themes.liveTitle')}
                </h3>
                {live.length === 0 ? (
                  <p className="text-sm text-fg-muted">{t('voicePage.themes.empty')}</p>
                ) : (
                  <ul className="space-y-2">{live.map(row)}</ul>
                )}
              </section>

              {archived.length > 0 && (
                <section className="space-y-3" aria-labelledby="voice-themes-archived">
                  <h3
                    id="voice-themes-archived"
                    className="text-sm font-semibold text-fg"
                  >
                    {t('voicePage.themes.archivedTitle')}
                  </h3>
                  <ul className="space-y-2">{archived.map(row)}</ul>
                </section>
              )}
            </>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
